/**
 * The family timeline: everything that was recorded or done, in time order,
 * with who did it.
 *
 * One selector for both clients, so the web page and the phone screen show
 * the same entries in the same order. Rows arrive as the stores hold them:
 * camelCase after sync, snake_case straight from the server; `field` reads
 * either, so the selector never cares which lens fed it.
 */

import { clipText } from '../ui'

export type TimelineKind =
  | 'capture'   // a voice or text note, or a check-in note against a block
  | 'mark'      // a block marked done / partial / skipped
  | 'note'      // a note on a block or a day
  | 'photo'     // a photo of her work
  | 'review'    // a day close or week check-in the parent filled in
  | 'sapling'   // Sapling's own review of a week
  | 'level'     // a skill level moved
  | 'material'  // something Sapling made

export interface TimelineEntry {
  id: string
  kind: TimelineKind
  /** Epoch ms when it happened. */
  at: number
  /** ISO day in the child's timezone; the grouping key. */
  date: string
  title: string
  body?: string
  /** The parent behind it; undefined means Sapling. */
  authorId?: string
  domainKey?: string
  domainName?: string
  /** Where to go from here. */
  ref: { captureId?: string; blockId?: string; artifactId?: string; reviewId?: string; skillKey?: string; date?: string }
  /** Marks: done | partial | skipped. Levels: the new level. */
  status?: string
  level?: number
  /** Photos: a thumbnail. */
  imageUrl?: string
}

export interface TimelineDay {
  date: string
  entries: TimelineEntry[]
}

type Row = Record<string, any>
type Rows = Record<string, Row> | Row[] | undefined

/** Read a field by its camelCase or snake_case name. */
export function field<T = any>(row: Row | undefined, camel: string, snake: string): T | undefined {
  if (!row) return undefined
  const v = row[camel]
  return (v === undefined ? row[snake] : v) as T | undefined
}

const live = (rows: Rows): Row[] =>
  (Array.isArray(rows) ? rows : Object.values(rows ?? {})).filter((r) => r && !r.deleted)

/** ISO calendar day of an instant in a timezone; the device's day when none. */
export function isoDayOf(ms: number, timezone?: string): string {
  if (timezone && typeof Intl !== 'undefined') {
    try {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit',
      }).format(new Date(ms))
    } catch {
      /* unknown zone: fall through */
    }
  }
  const d = new Date(ms)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export interface TimelineInput {
  childId: string
  timezone?: string
  captures?: Rows
  blocks?: Rows
  days?: Rows
  reviews?: Rows
  artifacts?: Rows
  levelHistory?: Rows
  skills?: Rows
  /** Only entries at or after this instant. */
  since?: number
}

const forChild = (rows: Rows, childId: string): Row[] =>
  live(rows).filter((r) => field(r, 'childId', 'child_id') === childId)

const truncate = (s: string | undefined, n: number): string | undefined => (s?.trim() ? clipText(s, n) : undefined)

/**
 * Everything recorded or done for a child, newest first, grouped by day.
 *
 * A block contributes at most two entries: its mark (when it was settled) and
 * its note. A capture logged against a block carries that block's title so a
 * voice note reads as "after Odd one out", not as a bare transcript. Photos
 * come from artifacts with an image; anything else Sapling made is a material.
 */
export function buildTimeline(input: TimelineInput): TimelineDay[] {
  const { childId, timezone } = input
  const since = input.since ?? 0
  const entries: TimelineEntry[] = []

  const blocks = forChild(input.blocks, childId)
  const blockById = new Map(blocks.map((b) => [b._id as string, b]))
  const artifacts = forChild(input.artifacts, childId)
  const artifactIds = new Set(artifacts.map((a) => a._id as string))
  const skills = forChild(input.skills, childId)
  const skillByKey = new Map(skills.map((s) => [s.key as string, s]))

  const blockDomain = (b?: Row) => ({
    domainKey: field<string>(b, 'domainKey', 'domain_key'),
    domainName: field<string>(b, 'domainName', 'domain_name'),
  })

  // ── captures ───────────────────────────────────────────────
  for (const c of forChild(input.captures, childId)) {
    const at = field<number>(c, 'occurredAt', 'occurred_at') ?? field<number>(c, 'capturedAt', 'captured_at') ?? 0
    if (at < since) continue
    const raw = c.raw ?? {}
    const text: string | undefined = raw.transcript ?? raw.text
    const source: string = c.source ?? 'text'
    const block = blockById.get(field<string>(c, 'blockRef', 'block_ref') ?? '')
    // A check-in note is already shown as the block's own note; the capture
    // row exists so it becomes evidence, not so it is read twice. Likewise a
    // photo's analysis capture: the photo entry carries the reading.
    if (source === 'checkin') continue
    if (raw.photo_artifact_id && artifactIds.has(raw.photo_artifact_id)) continue
    if (!text && !raw.photo_artifact_id) continue
    const kind: TimelineKind = source === 'photo' ? 'photo' : 'capture'
    const interp = c.interpretation
    const summary: string | undefined =
      interp && typeof interp === 'object' ? (interp.summary ?? interp.headline ?? undefined) : undefined
    entries.push({
      id: `capture:${c._id}`,
      kind,
      at,
      date: isoDayOf(at, timezone),
      title: block ? `After ${block.title}` : source === 'voice' ? 'Voice note' : 'Note',
      body: text ? text.trim() : summary,
      authorId: field<string>(c, 'authorId', 'author_id'),
      ...blockDomain(block),
      ref: { captureId: c._id, blockId: block?._id },
    })
  }

  // ── block marks and notes ──────────────────────────────────
  for (const b of blocks) {
    const status: string = b.status ?? 'pending'
    const updated = field<number>(b, 'updatedAt', 'updated_at') ?? 0
    const completedAt = field<number>(b, 'completedAt', 'completed_at')
    const authorId = field<string>(b, 'authorId', 'author_id')
    const note = b.note && String(b.note).trim() ? String(b.note).trim() : undefined
    // A note written while marking the block is the mark's own words, so it
    // rides on the mark. Only a note on a block still pending stands alone.
    if (status !== 'pending') {
      const at = completedAt ?? updated
      if (at >= since) {
        entries.push({
          id: `mark:${b._id}`,
          kind: 'mark',
          at,
          date: isoDayOf(at, timezone),
          title: b.title,
          status,
          body: note,
          authorId,
          ...blockDomain(b),
          ref: { blockId: b._id, date: b.date },
        })
      }
    } else if (note) {
      const at = updated
      if (at >= since) {
        entries.push({
          id: `note:${b._id}`,
          kind: 'note',
          at,
          date: isoDayOf(at, timezone),
          title: b.title,
          body: note,
          authorId,
          ...blockDomain(b),
          ref: { blockId: b._id, date: b.date },
        })
      }
    }
  }

  // ── day notes ──────────────────────────────────────────────
  for (const d of forChild(input.days, childId)) {
    if (!d.note || !String(d.note).trim()) continue
    const at = field<number>(d, 'updatedAt', 'updated_at') ?? 0
    if (at < since) continue
    entries.push({
      id: `daynote:${d._id}`,
      kind: 'note',
      at,
      date: isoDayOf(at, timezone),
      title: 'About the day',
      body: String(d.note).trim(),
      authorId: field<string>(d, 'authorId', 'author_id'),
      ref: { date: d.date },
    })
  }

  // ── reviews: the parent's half and Sapling's half ──────────
  for (const r of forChild(input.reviews, childId)) {
    const period: string = r.period ?? 'week'
    const parent = r.parent
    const submitted = field<number>(parent, 'submittedAt', 'submitted_at')
    if (parent && submitted && submitted >= since) {
      const parts = [
        parent.win ? `Win: ${parent.win}` : undefined,
        parent.adjust ? `Adjust: ${parent.adjust}` : undefined,
        parent.notes,
      ].filter(Boolean) as string[]
      entries.push({
        id: `review:${r._id}`,
        kind: 'review',
        at: submitted,
        date: isoDayOf(submitted, timezone),
        title: period === 'day' ? 'Closed the day' : period === 'cycle' ? 'Cycle check-in' : 'Week check-in',
        body: parts.join(' · ') || undefined,
        authorId: field<string>(r, 'authorId', 'author_id'),
        ref: { reviewId: r._id, date: r.date },
      })
    }
    const agent = r.agent
    const generated = field<number>(agent, 'generatedAt', 'generated_at')
    if (agent && generated && generated >= since) {
      entries.push({
        id: `sapling:${r._id}`,
        kind: 'sapling',
        at: generated,
        date: isoDayOf(generated, timezone),
        title: period === 'day' ? "Sapling's read of the day" : "Sapling's review of the week",
        body: truncate(agent.summary, 280),
        ref: { reviewId: r._id, date: r.date },
      })
    }
  }

  // ── level moves ────────────────────────────────────────────
  for (const h of forChild(input.levelHistory, childId)) {
    const at = h.at ?? field<number>(h, 'updatedAt', 'updated_at') ?? 0
    if (at < since) continue
    const key = field<string>(h, 'skillKey', 'skill_key') ?? ''
    const skill = skillByKey.get(key)
    const source: string = h.source ?? 'agent'
    entries.push({
      id: `level:${h._id}`,
      kind: 'level',
      at,
      date: isoDayOf(at, timezone),
      title: skill?.name ?? key,
      body: h.note ? String(h.note) : undefined,
      level: h.level,
      status: source,
      domainKey: field<string>(skill, 'domainKey', 'domain_key'),
      domainName: field<string>(skill, 'domainName', 'domain_name'),
      ref: { skillKey: key, captureId: field<string>(h, 'captureId', 'capture_id') },
    })
  }

  // ── artifacts: photos of her work, and what Sapling made ───
  for (const a of artifacts) {
    const at = field<number>(a, 'createdAt', 'created_at') ?? field<number>(a, 'updatedAt', 'updated_at') ?? 0
    if (at < since) continue
    const storage = a.storage ?? {}
    const mime: string | undefined = field(storage, 'mimeType', 'mime_type')
    const isImage = a.kind === 'photo' || (mime ?? '').startsWith('image/')
    const source: string = a.source ?? 'agent'
    if (isImage) {
      entries.push({
        id: `photo:${a._id}`,
        kind: 'photo',
        at,
        date: isoDayOf(at, timezone),
        title: a.title ?? 'Photo',
        body: truncate(a.analysis?.summary ?? a.summary, 200),
        authorId: field<string>(a, 'authorId', 'author_id'),
        imageUrl: storage.url,
        ref: { artifactId: a._id },
      })
      continue
    }
    if (source !== 'agent') continue
    if (a.kind === 'rubric' || a.kind === 'plan') continue
    if (a.superseded_by || a.supersededBy) continue
    // A build in flight or one that failed is not something that was made.
    const gen = a.generation
    if (a.draft || (gen && gen.status !== 'ready')) continue
    entries.push({
      id: `material:${a._id}`,
      kind: 'material',
      at,
      date: isoDayOf(at, timezone),
      title: a.title ?? 'Something new',
      body: truncate(a.summary, 160),
      status: a.kind,
      ref: { artifactId: a._id },
    })
  }

  entries.sort((x, y) => y.at - x.at)
  const days: TimelineDay[] = []
  for (const e of entries) {
    const last = days[days.length - 1]
    if (last && last.date === e.date) last.entries.push(e)
    else days.push({ date: e.date, entries: [e] })
  }
  return days
}

/** Entries of the kinds asked for; an empty set means everything. */
export function filterTimeline(days: TimelineDay[], kinds: Set<TimelineKind>): TimelineDay[] {
  if (!kinds.size) return days
  // Asking for notes means asking for the words: a mark that carries a
  // note is one of them.
  const keep = (e: TimelineEntry) => kinds.has(e.kind) || (kinds.has('note') && e.kind === 'mark' && !!e.body)
  return days
    .map((d) => ({ date: d.date, entries: d.entries.filter(keep) }))
    .filter((d) => d.entries.length)
}
